/**
 * SOLID Review
 * S — Strong. One concern: the tab's two identity-adjacent records in storage — the account's JWT (read, saved, cleared, and the
 *     player a valid one belongs to) and the stored game (`meyer_resume`) — each with its one-time legacy migration.
 * O — Good. A third record would be another group of methods here, not a change to the areas.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Two small groups of methods; a caller needing one does not reason about the other.
 * D — Strong. The storage areas are handed in (`IConfigDepBrowserStorages`), and the clock (one read, for `exp`; P4).
 */
import type { IConfigDepBrowserStorages } from './IServiceBrowserStorage';
import type { IServiceClock } from '../platform/IServiceClock';

const JWT_STORAGE_KEY = 'meyerlive3d_jwt';
const RESUME_SESSION_KEY = 'meyer_resume';

/** The stored game: the seat a reload or a discarded tab returns to. */
export interface IEntityResume {
  playerId: string;
  roomId: string;
  roomCode: string;
}

/**
 * THE SESSION'S TOKEN AND STORED GAME (frontend-infrastructure-oo P1, 2026-10-28, §28.286) — `sessionTokenStorage.ts`'s
 * functions as one object over the storage areas it is handed, built once by the composition root. Behaviour unchanged.
 *
 * The JWT is held in ONE place and never leaves Infrastructure (FE3, §28.199): `ClientAuthServer` saves it when a code is
 * accepted and clears it on sign-out; the GraphQL transports, the legal client and the payment calls read it from here.
 *
 * Both records are kept PER-TAB but DISCARD-DURABLE in the tab's own area (`shared/tabPersistentStorage`: a `window.name` tab id
 * and namespaced `localStorage`). That keeps §13.11's per-tab account isolation — two tabs can be two accounts, and a refresh in
 * one tab never picks up another's game — while surviving a mobile OS tab discard, which wipes `sessionStorage` and used to sign
 * the player out on return. The copies earlier builds kept in the session's and the browser's areas migrate on first read.
 */
export class SessionTokenStore {
  constructor(
    private readonly storages: IConfigDepBrowserStorages,
    /** What time it is — whether a token has expired (frontend-infrastructure-oo P4, §28.289). */
    private readonly clock: IServiceClock,
  ) {}

  /** The stored JWT, as stored — whether it is still valid is the server's call, or `account()`'s. */
  token(): string | null {
    const { tab, session, local } = this.storages;
    try {
      const jwt = tab.getItem(JWT_STORAGE_KEY);
      if (jwt) return jwt;
      // One-time migration from earlier implementations (sessionStorage, then the original shared localStorage) so existing
      // logged-in users aren't logged out.
      const legacyJwt = session.getItem(JWT_STORAGE_KEY) ?? local.getItem(JWT_STORAGE_KEY);
      if (legacyJwt) {
        tab.setItem(JWT_STORAGE_KEY, legacyJwt);
        session.removeItem(JWT_STORAGE_KEY);
        local.removeItem(JWT_STORAGE_KEY);
        return legacyJwt;
      }
      return null;
    } catch {
      return null;
    }
  }

  saveToken(jwt: string): void {
    const { tab, session, local } = this.storages;
    try {
      tab.setItem(JWT_STORAGE_KEY, jwt);
      session.removeItem(JWT_STORAGE_KEY); // drop legacy per-tab copy
      local.removeItem(JWT_STORAGE_KEY); // drop legacy shared copy
    } catch {
      // storage unavailable (private mode): the token lives for this call only
    }
  }

  clearToken(): void {
    const { tab, session, local } = this.storages;
    try {
      tab.removeItem(JWT_STORAGE_KEY);
      session.removeItem(JWT_STORAGE_KEY);
      local.removeItem(JWT_STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  /**
   * The player a stored token belongs to, while it is valid (the auth store's `hydrate`, moved): none, an unreadable token and an
   * expired one read as null; a token without a subject is forgotten. A token with no `exp` claim does not expire.
   */
  account(): { playerId: string } | null {
    const jwt = this.token();
    if (!jwt) return null;
    const claims = this.claimsOf(jwt);
    if (claims === null) return null;
    const exp = claims['exp'];
    if (typeof exp === 'number' && exp < Math.floor(this.clock.now() / 1000)) return null;
    const sub = claims['sub'];
    if (typeof sub !== 'string' || sub.length === 0) {
      this.clearToken();
      return null;
    }
    return { playerId: sub };
  }

  /** The stored game, or null when there is none or it is not a whole seat. */
  resume(): IEntityResume | null {
    const { tab, session, local } = this.storages;
    try {
      let raw = tab.getItem(RESUME_SESSION_KEY);
      if (!raw) {
        // One-time migration from the previous sessionStorage / shared-localStorage copies.
        const legacy = session.getItem(RESUME_SESSION_KEY) ?? local.getItem(RESUME_SESSION_KEY);
        if (legacy) {
          tab.setItem(RESUME_SESSION_KEY, legacy);
          session.removeItem(RESUME_SESSION_KEY);
          local.removeItem(RESUME_SESSION_KEY);
          raw = legacy;
        }
      }
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<IEntityResume>;
      if (parsed.playerId && parsed.roomId && parsed.roomCode) {
        return parsed as IEntityResume;
      }
      return null;
    } catch {
      return null;
    }
  }

  saveResume(resume: IEntityResume): void {
    const { tab, session, local } = this.storages;
    try {
      tab.setItem(RESUME_SESSION_KEY, JSON.stringify(resume));
      session.removeItem(RESUME_SESSION_KEY);
      local.removeItem(RESUME_SESSION_KEY);
    } catch {}
  }

  clearResume(): void {
    const { tab, session, local } = this.storages;
    try {
      tab.removeItem(RESUME_SESSION_KEY);
      session.removeItem(RESUME_SESSION_KEY);
      local.removeItem(RESUME_SESSION_KEY);
    } catch {}
  }

  /** The token's claims, or null when it is not three parts of base64url JSON. */
  private claimsOf(jwt: string): Record<string, unknown> | null {
    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) return null;
      const claims: unknown = JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')));
      return claims !== null && typeof claims === 'object' ? (claims as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
}
